import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

export default function ReportRenewals() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [propertyFilter, setPropertyFilter] = useState("all");
    const [downloading, setDownloading] = useState(false);
    const { current_staff } = useSelector((state) => state.auth);

    // Property mappings for display
    const propertyMappings = {
        dl: "Drivelines",
        es: "Eastside",
        sh: "Sovereign Heights",
        "w+a": "W+A",
        hh: "Highbury",
        nb: "Nota Bene",
        hos: "The House That Stanley Built",
        ns: "Nursery Square",
    };

    // Load data on mount
    useEffect(() => {
        loadData();
    }, [propertyFilter]);

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}reports/renewals/summary`, { params: { property: propertyFilter } });
            console.log("Renewals data received:", response.data);
            setData(response.data);
        } catch (error) {
            console.error("Error loading renewals data:", error);
            console.error("Error details:", error.response?.data);
            toast.error("Failed to load renewals data: " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const response = await axios({
                method: "get",
                url: `${process.env.REACT_APP_API_URL}reports/export_renewals`,
                responseType: "blob",
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `renewals_report_${new Date().toISOString().split("T")[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success("Report downloaded successfully!");
        } catch (error) {
            console.error("Error downloading report:", error);
            toast.error("Failed to download report");
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadOriginal = async () => {
        setDownloading(true);
        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}reports/export_renewals`,
                data: { staff_id: current_staff?.id },
            });

            if (response.data.success) {
                toast.success(response.data.message || "Original report is being generated. You will receive an email shortly.");
            }
        } catch (error) {
            console.error("Error requesting original report:", error);
            toast.error("Failed to request original report");
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="w-auto bg-gray-100 my-6 xl:my-0 xl:mx-4 rounded-3xl flex-1 p-8">
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                        <span className="ml-3 text-gray-600">Loading renewals data...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-auto bg-gray-100 my-6 xl:my-0 xl:mx-4 rounded-3xl flex-1 p-8">
            <div className="bg-white rounded-xl shadow-sm p-6">
                {/* Header with Download Buttons */}
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Renewals Report</h1>
                    <div className="flex gap-3">
                        {/* Original Report Button (Email) */}
                        <button
                            onClick={handleDownloadOriginal}
                            disabled={downloading}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                />
                            </svg>
                            Email Original Report
                        </button>

                        {/* Fast Download Button */}
                        <button
                            onClick={handleDownload}
                            disabled={downloading}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary_hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {downloading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                    Downloading...
                                </>
                            ) : (
                                <>
                                    <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                    </svg>
                                    Download Excel (Fast)
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Property Breakdown Table - Matches Excel Format */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                                    Metric
                                </th>
                                {data?.properties &&
                                    Object.values(data.properties).map((prop) => (
                                        <th key={prop.short_code} scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {prop.short_code}
                                        </th>
                                    ))}
                                <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider bg-gray-100">
                                    TOTAL
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {/* Renewal Messages Sent */}
                            <tr>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">Number of renewal message sent out</td>
                                {data?.properties &&
                                    Object.values(data.properties).map((prop) => (
                                        <td key={prop.short_code} className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                                            {prop.renewal_messages_sent?.toLocaleString() || 0}
                                        </td>
                                    ))}
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-bold text-gray-900 bg-gray-100">{data?.totals?.renewal_messages_sent?.toLocaleString() || 0}</td>
                            </tr>

                            {/* Signed Renewal Leases */}
                            <tr className="bg-red-50">
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-red-50 z-10">Signed Renewal lease</td>
                                {data?.properties &&
                                    Object.values(data.properties).map((prop) => (
                                        <td key={prop.short_code} className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                                            {prop.signed_renewal_leases?.toLocaleString() || 0}
                                        </td>
                                    ))}
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-bold text-gray-900 bg-gray-100">{data?.totals?.signed_renewal_leases?.toLocaleString() || 0}</td>
                            </tr>

                            {/* Remind Me Again */}
                            <tr>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">Remind me again</td>
                                {data?.properties &&
                                    Object.values(data.properties).map((prop) => (
                                        <td key={prop.short_code} className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                                            {prop.remind_me_again?.toLocaleString() || 0}
                                        </td>
                                    ))}
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-bold text-gray-900 bg-gray-100">{data?.totals?.remind_me_again?.toLocaleString() || 0}</td>
                            </tr>

                            {/* Not Renewing */}
                            <tr>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">Not Renewing</td>
                                {data?.properties &&
                                    Object.values(data.properties).map((prop) => (
                                        <td key={prop.short_code} className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                                            {prop.not_renewing?.toLocaleString() || 0}
                                        </td>
                                    ))}
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-bold text-gray-900 bg-gray-100">{data?.totals?.not_renewing?.toLocaleString() || 0}</td>
                            </tr>

                            {/* Renewals to Chase */}
                            <tr>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">Renewals to chase</td>
                                {data?.properties &&
                                    Object.values(data.properties).map((prop) => (
                                        <td key={prop.short_code} className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                                            {prop.renewals_to_chase?.toLocaleString() || 0}
                                        </td>
                                    ))}
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-bold text-gray-900 bg-gray-100">{data?.totals?.renewals_to_chase?.toLocaleString() || 0}</td>
                            </tr>

                            {/* Renewal Percentage */}
                            <tr className="bg-green-50">
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-green-50 z-10">Renewal %</td>
                                {data?.properties &&
                                    Object.values(data.properties).map((prop) => {
                                        const messagesSent = prop.renewal_messages_sent || 0;
                                        const signed = prop.signed_renewal_leases || 0;
                                        const percentage = messagesSent > 0 ? Math.round((signed / messagesSent) * 100) : 0;
                                        return (
                                            <td key={prop.short_code} className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                                                {percentage}%
                                            </td>
                                        );
                                    })}
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-bold text-gray-900 bg-green-100">
                                    {data?.totals?.renewal_messages_sent > 0 ? Math.round((data.totals.signed_renewal_leases / data.totals.renewal_messages_sent) * 100) : 0}%
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Info Note */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-blue-700">
                                <strong>Note:</strong> This report shows real-time data. Click "Download Excel" to get a detailed breakdown by property with full booking lists.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
